import { observable, action, computed, when } from "mobx";

import {
	EventType,
	LiveStatus,
	EventOffer as EventOfferResponse,
} from "@gp/models";
import {
	PrematchOfferStore,
	EventOffer,
	SportOffer,
	getEventSortByFavorite,
} from "@gp/offer";
import { HubState } from "@gp/hub";
import { ConsoleLogger, LogLevel, Sorter } from "@gp/utility";

import { getCurrentLanguage } from "@utils";
import betRules from "@betting-rules";

import { BettingTypeSelectorsStore } from "@state/stores/offer/shared/BettingTypeSelectorsStore";
import { LoaderStore } from "@state/stores/common";
import {
	HomePageStoreConstructorsParameters,
	ISportSliderHeaderStore,
	StoreTypeEnum,
} from "./ISportSliderHeaderStore";
import { IStoreLifeCycle } from "@interface/IStoreLifeCycle";
import FavoriteStore from "@state/stores/offer/shared/FavoritesStore";
import type { RootStore } from "@state/stores/types";

export class MostPlayedViewStore
	extends PrematchOfferStore
	implements ISportSliderHeaderStore, IStoreLifeCycle
{
	type = StoreTypeEnum.MostPlayedViewStore;
	private loader: LoaderStore;
	bettingTypesByAbrv: string;
	rootStore: RootStore;
	private favoriteStore: FavoriteStore;
	private homeOfferLoader: LoaderStore;

	public route = "most-played";
	@observable public isStoreInitialized = false;
	@observable public isFetchingData = false;
	@observable public selectedSportId: string;

	@computed public get shouldDisplayInitialLoader() {
		return !this.isStoreInitialized;
	}

	@computed public get isLoading() {
		return this.loader.isLoading;
	}

	@computed public get isLoadingProcess() {
		return this.loader.isLoadingProcess;
	}
	@computed get eventsList(): (EventOffer & { sport: SportOffer })[] {
		const eventList = (
			this.events as (EventOffer & { sport: SportOffer })[]
		)
			.filter((e) => e.sportId === this.selectedSportId)
			.map((e) => {
				e.sport = this.getMappedSport(e);
				return e;
			});
		if (
			this.favoriteStore &&
			this.favoriteStore.userFavoriteEventsSet.size > 0
		) {
			return eventList.sort(
				Sorter.sort((a, b) => {
					return Sorter.sort(
						(a1: EventOffer, b1: EventOffer) =>
							getEventSortByFavorite(a1, b1, this.favoriteStore),
						"startTime",
						"id"
					)(a, b);
				})
			);
		}

		return eventList;
	}

	@computed get isEmpty() {
		return (
			this.sportsList.length === 0 &&
			this.eventsList.length === 0 &&
			!this.isFetchingData &&
			!this.isLoading &&
			this.isStoreInitialized
		);
	}

	@computed get eventIdsForEventSwitcher() {
		const list = this.eventsList.map(({ id }) => id);

		return list;
	}

	@observable public sportsList: ISportSliderHeaderStore["sportsList"] = [];

	constructor(config: HomePageStoreConstructorsParameters) {
		super({
			logger: new ConsoleLogger(false, LogLevel.ERROR),
			removeDelay: 10,
			throttle: 4,
			enableThrottling: true,
			pageSize: 5,
			bettingTypeSelectorsStore: new BettingTypeSelectorsStore(),
			customConfiguration: betRules,
		});

		this.rootStore = config.rootStore;
		this.favoriteStore = config.favoritesStore;
		this.homeOfferLoader = config.loaderStore;

		this.loader = new LoaderStore();
		this.getBettingTypes = this.getBettingTypes.bind(this);
	}

	@action.bound
	async onInitialize(sportAbrv?: string) {
		this.isFetchingData = true;
		this.homeOfferLoader.suspend();
		await this.fetchSport(sportAbrv);
	}

	@action.bound
	async fetchSport(sportAbrv?: string) {
		this.isFetchingData = true;
		const allBts = this.getBettingTypes();
		try {
			// Need to start connection manually here since fire and forget hub
			// requests don't create a dispatcher.
			this.rootStore.offerHub.startSession();
			await when(
				() =>
					this.rootStore.offerHub.connectionState ===
					HubState.CONNECTED
			);

			this.loader.suspend();

			const sportId = this.selectedSportId;
			const offer = await this.rootStore.hub.getMostPlayedBySport({
				language: getCurrentLanguage(),
				numberOfEvents: 15,
				sportId,
				filter: {
					eventType: EventType.NORMAL,
					liveStatus: LiveStatus.PREMATCH,
					offers: [
						{
							bettingType: {
								abrv: {
									eq: allBts.normal,
								},
							},
						},
						{
							bettingType: {
								abrv: {
									eq: allBts.marginal,
								},
							},
							isFavorite: true,
						},
					],
				},
			});

			const offers = offer.selectedSportGroup.events.events.reduce<
				EventOfferResponse[]
			>((acc, curr) => {
				if (curr.event == null) {
					return acc;
				}

				acc.push(curr.event);
				return acc;
			}, []);

			this.sportsList = offer.sports.map((sp) => ({
				...sp.sport,
				count: sp.count,
			}));
			this.assignOfferData({
				lookup: offer.selectedSportGroup.lookup,
				offer: offers,
				pageNumber: 1,
				pageSize: 1,
				totalNumberOfItems: 1,
				startingVersion: 0,
				version: 1,
			});
			if (
				this.eventsInSports.sports.length > 0 &&
				(!this.selectedSportId ||
					!this.eventsInSports.sports.some(
						(sp) => sp.id === this.selectedSportId
					))
			)
				if (sportAbrv) {
					const sport = this.sportsList.find(
						(sport) => sport.abrv === sportAbrv
					);
					if (sport) {
						await this.setSelectedSportId(sport.id);
					}
				} else {
					await this.setSelectedSportId(this.sportsList[0].id);
				}
		} catch (err) {
			console.error("Error while fetching most played events", err);
		}
		this.loader.resume();
		this.homeOfferLoader.resume();
		this.isStoreInitialized = true;
		this.isFetchingData = false;
	}

	public getBettingTypes() {
		return this.OfferMapper.getBettingTypes("prematch", betRules);
	}

	//#region actions

	@action.bound
	public onDispose(): void {
		this.isStoreInitialized = false;
		this.isFetchingData = false;
		this.selectedSportId = "";
		this.reset();
	}

	@action.bound
	public async setSelectedSportId(newSport: string) {
		this.selectedSportId = newSport;
		await this.fetchSport(newSport);
	}

	//#endregion actions
}
